import React from 'react';
import './LegalPage.css';

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy for Pholtr</h1>
        <div className="meta">Last updated: <strong>2025-10-24</strong></div>

        <h2>1. Introduction</h2>
        <p>This Privacy Policy describes how Hibou AB ("we", "us") collects, uses, stores, and protects your personal data when you use the application <strong>Pholtr</strong> (the "App").</p>
        <p>We value your privacy and process your personal data in accordance with applicable data protection legislation, including the <strong>General Data Protection Regulation (GDPR)</strong>.</p>

        <h2>2. Data Controller</h2>
        <p><strong>Hibou AB</strong><br />
          Organization number: <strong>559111-5505</strong><br />
          Address: <span className="placeholder">Hammarbyterrassen 26, 120 63 Stockholm, Sweden</span><br />
          Email (privacy inquiries): <span className="placeholder">info@pholtr.com</span>
        </p>

        <h2>3. What Personal Data Do We Collect?</h2>
        <p>We collect the following types of data from you:</p>

        <h3>A. Image and Content Data</h3>
        <ul>
          <li><strong>Uploaded Images:</strong> The images you upload to Pholtr for modification. These may be considered personal data if they contain identifiable individuals.</li>
          <li><strong>Modified Images:</strong> The images generated by Pholtr after changes to the background, lighting, etc., have been applied.</li>
        </ul>

        <h3>B. User Data</h3>
        <ul>
          <li><strong>Technical Data:</strong> Information about the device you use (e.g., model, operating system, IP address, unique device identifiers).</li>
          <li><strong>Usage Data:</strong> Information about how you use Pholtr, such as which features you use, when and for how long you use the App, and app crashes and performance.</li>
          <li><strong>Account Information (if applicable):</strong> If you register an account, we collect your email address, username, and hashed password.</li>
        </ul>

        <h2>4. How and Why We Process Your Personal Data (Purpose and Legal Basis)</h2>
        <p>The table below summarises the types of data we process, the purposes, and the legal basis under the GDPR.</p>

        <table aria-label="Processing purposes and legal basis">
          <thead>
            <tr>
              <th>Type of Data</th>
              <th>Purpose of Processing</th>
              <th>Legal Basis (under GDPR)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Uploaded and Modified Images</td>
              <td>To provide the image modification service you have requested.</td>
              <td>Performance of a contract: The processing is necessary to deliver the service you are using.</td>
            </tr>
            <tr>
              <td>Uploaded Images (Anonymized/Depersonalized)</td>
              <td>To improve and develop Pholtr's modification algorithms and functionality.</td>
              <td>Legitimate interest: Our interest in improving our service outweighs the user's interest in protection, provided the images are depersonalized as much as possible.</td>
            </tr>
            <tr>
              <td>Technical and Usage Data</td>
              <td>To analyze usage trends, monitor Pholtr's performance, and troubleshoot issues.</td>
              <td>Legitimate interest: To ensure Pholtr's functionality, security, and continuous improvement.</td>
            </tr>
            <tr>
              <td>Account Information</td>
              <td>To manage your account, provide support, and communicate with you.</td>
              <td>Performance of a contract: To administer your use of the service.</td>
            </tr>
          </tbody>
        </table>

        <h2>5. How Long Do We Store Your Data?</h2>
        <p>We only store your personal data for as long as it is necessary for the purposes for which it was collected, or as required by law.</p>
        <ul>
          <li><strong>Uploaded and Modified Images:</strong> Your images are deleted from our servers <span className="placeholder">immediately after processing</span>. We only retain the final modified images on your device if you choose to not delete them.</li>
          <li><strong>Account Information:</strong> Stored until you close your account.</li>
          <li><strong>Usage Data and Technical Data:</strong> Stored as long as necessary for analysis purposes, usually a maximum of <span className="placeholder">24 months</span>.</li>
        </ul>

        <h2>6. Sharing of Personal Data</h2>
        <p>We do not share your personal data with unauthorized third parties. However, we may share data with the following categories of recipients:</p>
        <ul>
          <li><strong>Service Providers:</strong> We may hire external providers for the operation, storage (hosting), and analysis of Pholtr. These providers may only process personal data according to our instructions and may not use it for their own purposes (for example: cloud services used for storage and image processing).</li>
          <li><strong>Authorities:</strong> If required by law, court order, or other legal process.</li>
        </ul>

        <h2>7. Transfer to Third Countries</h2>
        <p>We strive to process your data within the EU/EEA. If we transfer data to countries outside the EU/EEA (so-called "third countries"), we ensure an adequate level of protection by using standard contractual clauses approved by the European Commission or by transferring to a country that the European Commission has deemed to have an adequate level of protection.</p>

        <h2>8. Your Rights (GDPR)</h2>
        <p>Under GDPR, you have the following rights:</p>
        <ul>
          <li><strong>Right of Access (Data Subject Request):</strong> You have the right to obtain confirmation as to whether or not personal data concerning you are being processed, and, where that is the case, access to the personal data.</li>
          <li><strong>Right to Rectification:</strong> You have the right to have inaccurate personal data corrected without undue delay.</li>
          <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> You have the right to have your personal data erased under certain circumstances, for example, if the data is no longer necessary for the purposes for which it was collected.</li>
          <li><strong>Right to Restriction of Processing:</strong> You have the right to request that our processing of your personal data be restricted.</li>
          <li><strong>Right to Object:</strong> You have the right to object to processing that is based on our legitimate interest.</li>
          <li><strong>Right to Data Portability:</strong> You have the right to receive your personal data in a structured, commonly used, and machine-readable format.</li>
        </ul>
        <p>To exercise your rights, please contact us via the email address listed under <strong>Section 2</strong>.</p>

        <h2>9. Complaints</h2>
        <p>If you believe that we are processing your personal data incorrectly, you have the right to lodge a complaint with the Swedish Authority for Privacy Protection (IMY), the supervisory authority for data protection in Sweden.</p>

        <h2>10. Changes to the Privacy Policy</h2>
        <p>We reserve the right to update this policy as needed. The latest version will always be published in Pholtr and on our website. If the changes are significant, we will inform you via Pholtr or email.</p>

        <footer>
          <p className="small">If you need this policy in another language or in an accessible format, please contact us at the email address listed under Section 2.</p>
        </footer>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
